import React, { useState, useEffect, useCallback, useMemo } from "react";
import { format } from "date-fns";
import {
  CheckCircle2,
  AlertTriangle,
  Info,
  X,
  Loader2,
  Search,
  Eye,
  RefreshCw,
  Bell,
} from "lucide-react";

import StatCard from "@/shared/components/dashboard/StatCard";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import {
  getMyNotifications,
  markNotificationRead,
  removeNotification as apiRemoveNotification,
} from "@/shared/api/notifications";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/shared/components/ui/use-toast";
import {
  typeConfig,
  getTranslatedNotification,
} from "@/shared/utils/notificationUtils";
import { Pagination } from "@/shared/components/shared/Pagination";
import { NotificationCard } from "@/shared/components/notifications/NotificationCard";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

// ─── Constants ────────────────────────────────────────────────────────────────
const ITEMS_PER_PAGE = 20;

// ─── API Data Mapper ───────────────────────────────────────────────────────────
// Raw API response ko app format mein convert karta hai
const mapFromApi = (n = {}) => ({
  id: n.id,
  title: n.title || "",
  message: n.message || "",
  type: String(n.type || "SYSTEM").toUpperCase(),
  read: Boolean(n.isRead),
  time: n.createdAt ? new Date(n.createdAt) : new Date(),
});

// ─── Component ────────────────────────────────────────────────────────────────
export default function Notifications({
  allowedTypes = null,
  pageDescription = null,
}) {
  const { t } = useLanguage();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // ── State ──────────────────────────────────────────────────────────────────
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState("all");
  const [clearingAll, setClearingAll] = useState(false);
  const [actionLoadingIds, setActionLoadingIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  // ── Toast Helpers ──────────────────────────────────────────────────────────
  // Har jagah toast object repeat likhne se bachao
  const toastSuccess = useCallback(
    (msgKey, fallback) =>
      toast({
        title: t("success") || "Success",
        description: t(msgKey) || fallback,
        className: "rounded-xl",
      }),
    [toast, t],
  );

  const toastError = useCallback(
    (msgKey, fallback) =>
      toast({
        title: t("error") || "Error",
        description: t(msgKey) || fallback,
        variant: "destructive",
        className: "rounded-xl",
      }),
    [toast, t],
  );

  // ── Derived Data (useMemo) ─────────────────────────────────────────────────

  // allowedTypes ko ek baar normalize karo — filtering mein fast lookup ke liye
  const normalizedAllowedTypes = useMemo(() => {
    if (!Array.isArray(allowedTypes) || allowedTypes.length === 0) return null;
    return new Set(allowedTypes.map((t) => String(t).toUpperCase()));
  }, [allowedTypes]);

  // Search + type filter apply karo, translate bhi karo
  const filteredNotifications = useMemo(() => {
    const search = (searchTerm || "").toLowerCase();
    return notifications
      .map((n) => getTranslatedNotification(n, t))
      .filter((n) => {
        if (selectedType !== "all" && n.type !== selectedType) return false;
        if (!search) return true;
        return [
          n.title,
          n.message,
          n.type,
          format(n.time, "MMM dd, yyyy h:mm a"),
        ]
          .join(" ")
          .toLowerCase()
          .includes(search);
      });
  }, [notifications, selectedType, searchTerm, t]);

  // Total / unread / read counts
  const stats = useMemo(() => {
    const total = notifications.length;
    const unread = notifications.filter((n) => !n.read).length;
    return { total, unread, read: total - unread };
  }, [notifications]);

  // Dropdown mein har type ke sath count dikhao
  const typeCountMap = useMemo(
    () => ({
      all: stats.total,
      WITHDRAWAL: notifications.filter((n) => n.type === "WITHDRAWAL").length,
      DEPOSIT: notifications.filter((n) => n.type === "DEPOSIT").length,
      SYSTEM: notifications.filter((n) => n.type === "SYSTEM").length,
    }),
    [notifications, stats.total],
  );

  // Sirf allowed types ke options dikhao
  const typeOptions = useMemo(
    () =>
      [
        { value: "all", label: t("all") || "All" },
        { value: "WITHDRAWAL", label: t("withdrawal") || "Withdrawal" },
        { value: "DEPOSIT", label: t("deposits") || "Deposits" },
        { value: "SYSTEM", label: t("systemNotification") || "System" },
      ].filter(
        (o) =>
          o.value === "all" ||
          !normalizedAllowedTypes ||
          normalizedAllowedTypes.has(o.value),
      ),
    [normalizedAllowedTypes, t],
  );

  // Stats cards config — render mein seedha map hoga
  const statsConfig = useMemo(
    () => [
      {
        key: "total",
        title: t("total") || "Total",
        icon: Bell,
        iconColor: "bg-blue-500",
        value: stats.total,
      },
      {
        key: "unread",
        title: t("unread") || "Unread",
        icon: Info,
        iconColor: "bg-amber-500",
        value: stats.unread,
      },
      {
        key: "read",
        title: t("read") || "Read",
        icon: CheckCircle2,
        iconColor: "bg-emerald-500",
        value: stats.read,
      },
    ],
    [stats, t],
  );

  // Pagination slice
  const totalPages = Math.ceil(filteredNotifications.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;

  const paginatedNotifications = useMemo(
    () => filteredNotifications.slice(startIndex, endIndex),
    [filteredNotifications, startIndex, endIndex],
  );

  // ── Actions (useCallback) ──────────────────────────────────────────────────

  // API se notifications fetch karo
  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await getMyNotifications();
      const mapped = (list || []).map(mapFromApi);
      const result = normalizedAllowedTypes
        ? mapped.filter((n) => normalizedAllowedTypes.has(n.type))
        : mapped;
      setNotifications(result);
      setLastUpdatedAt(new Date());
    } catch (err) {
      const msg =
        err?.message ||
        t("failedToLoadNotifications") ||
        "Failed to load notifications";
      setError(msg);
      setNotifications([]);
      toastError("failedToLoadNotifications", msg);
    } finally {
      setLoading(false);
    }
  }, [normalizedAllowedTypes, t, toastError]);

  // Ek notification read mark karo
  const markAsRead = useCallback(
    async (id) => {
      if (!id) return;
      setActionLoadingIds((prev) => [...prev, id]);
      try {
        const ok = await markNotificationRead(id);
        if (!ok) {
          toastError("errorMarkingAsRead", "Failed to Mark as Read");
          return;
        }

        setNotifications((prev) =>
          prev.map((n) => (n.id === id ? { ...n, read: true } : n)),
        );
        queryClient.invalidateQueries({ queryKey: ["myNotifications"] });
        toastSuccess("notificationMarkedAsRead", "Notification Marked as Read");
      } catch {
        toastError("errorMarkingAsRead", "Failed to Mark as Read");
      } finally {
        setActionLoadingIds((prev) => prev.filter((i) => i !== id));
      }
    },
    [queryClient, toastError, toastSuccess],
  );

  // Ek notification remove karo
  const removeNotification = useCallback(
    async (id) => {
      if (!id) return;
      try {
        const ok = await apiRemoveNotification(id);
        if (!ok) {
          toastError(
            "errorRemovingNotification",
            "Failed to Remove Notification",
          );
          return;
        }

        setNotifications((prev) => prev.filter((n) => n.id !== id));
        toastSuccess("notificationRemoved", "Notification Removed");
      } catch {
        toastError(
          "errorRemovingNotification",
          "Failed to Remove Notification",
        );
      }
    },
    [toastError, toastSuccess],
  );

  // Sab notifications read mark karo
  const markAllAsRead = useCallback(async () => {
    const unread = notifications.filter((n) => !n.read);
    if (!unread.length) {
      toastSuccess(
        "allNotificationsAlreadyRead",
        "All Notifications Already Read",
      );
      return;
    }
    try {
      await Promise.all(unread.map((n) => markNotificationRead(n.id)));
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      toastSuccess(
        "allNotificationsMarkedRead",
        "All Notifications Marked as Read",
      );
    } catch {
      toastError("failedToMarkAsRead", "Failed to Mark as Read");
    }
  }, [notifications, toastError, toastSuccess]);

  // Sab notifications delete karo
  const clearAllNotifications = useCallback(async () => {
    if (!notifications.length) {
      toastSuccess("noNotificationsToClear", "No Notifications to Clear");
      return;
    }
    setClearingAll(true);
    try {
      const snapshot = [...notifications];
      const results = await Promise.allSettled(
        snapshot.map((n) => apiRemoveNotification(n.id)),
      );

      // Sirf woh IDs nikalo jinke delete successful rahi
      const removedIds = new Set(
        snapshot
          .filter(
            (_, i) => results[i].status === "fulfilled" && results[i].value,
          )
          .map((n) => n.id),
      );

      if (removedIds.size > 0) {
        setNotifications((prev) => prev.filter((n) => !removedIds.has(n.id)));
        queryClient.invalidateQueries({ queryKey: ["myNotifications"] });
      }

      removedIds.size === snapshot.length
        ? toastSuccess("allNotificationsCleared", "All Notifications Cleared")
        : toastError(
            "someNotificationsCouldNotBeRemoved",
            "Some Notifications Could Not Be Removed",
          );
    } catch {
      toastError("failedToRemoveNotification", "Failed to Remove Notification");
    } finally {
      setClearingAll(false);
    }
  }, [notifications, queryClient, toastError, toastSuccess]);

  // ── Side Effects ───────────────────────────────────────────────────────────

  // Mount pe aur jab allowed types change hon — fetch karo
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Search ya filter change ho — page 1 pe wapas jao
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedType]);

  // Agar current page total pages se zyada ho jaye — fix karo
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header — Title + Last Updated + Refresh button */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 mb-1">
            {t("notifications") || "Notifications"}
          </h1>
          <p className="text-sm text-slate-500">
            {pageDescription ||
              t("allNotifications") ||
              "View all your notifications including withdrawals and deposits"}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-xs text-slate-500 hidden sm:block">
            {lastUpdatedAt
              ? `${t("lastUpdated") || "Last Updated"}: ${format(lastUpdatedAt, "MMM dd, h:mm a")}`
              : t("notUpdatedYet") || "Not Updated Yet"}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchNotifications}
            disabled={loading}
            className="gap-2 bg-white"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            {t("refresh") || "Refresh"}
          </Button>
        </div>
      </div>

      {/* Stats Cards — Total / Unread / Read */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading
          ? Array.from({ length: 3 }).map((_, i) => (
              <Skeleton
                key={i}
                height="100%"
                width="100%"
                borderRadius="1rem"
                containerClassName="block h-32 w-full"
              />
            ))
          : statsConfig.map((item) => (
              <StatCard
                key={item.key}
                title={item.title}
                value={item.value}
                icon={item.icon}
                iconColor={item.iconColor}
              />
            ))}
      </div>

      {/* Search + Type Filter */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder={
                t("searchForNotifications") || "Search notifications..."
              }
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-700">
              {t("type") || "Type"}:
            </span>
            <Select value={selectedType} onValueChange={setSelectedType}>
              <SelectTrigger className="bg-slate-50 border-slate-200">
                <SelectValue placeholder={t("type") || "Type"} />
              </SelectTrigger>
              <SelectContent>
                {typeOptions.map((option) => {
                  const config = typeConfig[option.value] || typeConfig.SYSTEM;
                  const Icon = option.value === "all" ? Info : config.icon;
                  return (
                    <SelectItem key={option.value} value={option.value}>
                      <div className="flex items-center justify-between w-full gap-2">
                        <span className="flex items-center gap-2 text-sm">
                          <Icon className="h-3.5 w-3.5 text-slate-500" />
                          {option.label}
                        </span>
                        <span className="text-xs text-slate-500">
                          {typeCountMap[option.value] || 0}
                        </span>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Action Buttons — Mark All Read / Clear All */}
      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={markAllAsRead}
          disabled={clearingAll || stats.unread === 0}
          className="gap-2"
        >
          <Eye className="h-4 w-4" />
          {t("markAllRead") || "Mark All Read"}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={clearAllNotifications}
          disabled={clearingAll || stats.total === 0}
          className="gap-2 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
        >
          {clearingAll ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <X className="h-4 w-4" />
          )}
          {t("clearAll") || "Clear all"}
        </Button>
      </div>

      {/* Notifications List */}
      <div className="space-y-4">
        {/* Error Banner */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm font-medium">Error: {error}</p>
          </div>
        )}

        {/* Loading Skeletons */}
        {loading ? (
          <div className="grid gap-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="p-4 rounded-xl border bg-white border-slate-200"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 shrink-0">
                    <Skeleton height="100%" borderRadius="0.5rem" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Skeleton width={150} height={16} />
                      <Skeleton width={80} height={20} borderRadius="9999px" />
                    </div>
                    <Skeleton width="100%" height={14} className="mb-2" />
                    <Skeleton width={120} height={12} />
                  </div>
                  <div className="flex gap-2">
                    <Skeleton width={32} height={32} borderRadius="0.375rem" />
                    <Skeleton width={32} height={32} borderRadius="0.375rem" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : /* Empty State */
        filteredNotifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-2">
            <AlertTriangle className="h-8 w-8 text-slate-300" />
            <p className="text-slate-500">
              {searchTerm || selectedType !== "all"
                ? t("noNotifications") ||
                  "No notifications matching your filters"
                : t("noNotifications") || "No notifications yet"}
            </p>
          </div>
        ) : (
          /* Notification Cards + Pagination */
          <>
            <div className="grid gap-3">
              {paginatedNotifications.map((notification) => (
                <NotificationCard
                  key={notification.id}
                  notification={notification}
                  isLoading={actionLoadingIds.includes(notification.id)}
                  onMarkAsRead={markAsRead}
                  onRemove={removeNotification}
                  t={t}
                />
              ))}
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              startIndex={startIndex}
              endIndex={endIndex}
              totalItems={filteredNotifications.length}
              t={t}
              itemName="notifications"
            />
          </>
        )}
      </div>
    </div>
  );
}
